import React, { useCallback } from "react";
import { useIntl } from "react-intl";

import { Pencil, Plus, CircleCheck } from "@labxchange/icons";

import { TableCellProps, ButtonClickParams } from "../types";
import {
  buttonCellClass,
  buttonCellButtonClass,
  buttonCellContentClass,
  buttonCellTextClass,
} from "../TableQuestion.css";

export interface ButtonCellProps
  extends Pick<
    TableCellProps,
    "cellId" | "rowIndex" | "colIndex" | "colspan" | "disabled" | "text"
  > {
  buttonIcon?: "pencil" | "plus" | "check";
  buttonAriaLabel?: string;
  actionId?: string;
  onButtonClick?: (params: Omit<ButtonClickParams, "componentID">) => void;
}

const ICON_MAP = {
  pencil: Pencil,
  plus: Plus,
  check: CircleCheck,
} as const;

export const ButtonCell: React.FC<ButtonCellProps> = ({
  cellId,
  rowIndex,
  colIndex,
  colspan,
  disabled,
  text,
  buttonIcon = "pencil",
  buttonAriaLabel,
  actionId,
  onButtonClick,
}) => {
  const intl = useIntl();

  const handleClick = useCallback(() => {
    if (!disabled) {
      onButtonClick?.({
        rowId: rowIndex,
        colId: colIndex,
        actionId,
      });
    }
  }, [onButtonClick, rowIndex, colIndex, actionId, disabled]);

  const Icon = ICON_MAP[buttonIcon];

  const translatedAriaLabel = buttonAriaLabel
    ? intl.formatMessage({
        id: buttonAriaLabel,
        defaultMessage: buttonAriaLabel,
      })
    : undefined;

  const displayText = text ?? "-";

  return (
    <td
      key={cellId}
      className={buttonCellClass}
      colSpan={colspan ? Number(colspan) : undefined}
    >
      <div className={buttonCellContentClass}>
        <span className={buttonCellTextClass}>{displayText}</span>
        <button
          type="button"
          className={buttonCellButtonClass}
          onClick={handleClick}
          disabled={disabled}
          aria-label={translatedAriaLabel}
        >
          <Icon size={18} />
        </button>
      </div>
    </td>
  );
};
